import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDoubleRight } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { useCreateMutation } from "./Settings.state";
import { useUserQuery } from "../monitor/User.state";
import {
  Input,
  RadioGroup,
  Spinner,
  RowInfo,
  FormAutocomplete,
  HelpModal,
} from "../common";
import { Button, ButtonWrapper, Col, Row } from "../App.styles";
import {
  CategoryModal,
  ChallengeListModal,
  ChatModal,
  LateSubmissionsModal,
  LeaderboardModal,
  ModerationModal,
  PlayerExportsModal,
  PromotionModal,
  SearchModal,
  SharingLocationsModal,
  TeamsModal,
  TimeToCompleteModal,
  UnlockStyleModal,
} from "./modals";
import WebhookModal from "./modals/WebhookModal";
import ChallengeViewModal from './modals/ChallengeViewModal'
import ChallengeDropInViewModal from './modals/ChallengeDropInViewModal'
import { useDecision } from '@optimizely/react-sdk'
import CheatCodes from "./CheatCodes";
import CheatCodeModal from "./modals/CheatCodeModal";

const Optional = ({ useQuery }) => {
  const { t } = useTranslation()
  const { game_id } = useParams();
  const history = useHistory();
  const { isCreating, create } = useCreateMutation();
  const { super_admin } = useUserQuery();
  const [decisionMetric] = useDecision('leaderboard-metric');
  const [game] = useQuery();

  const onCreate = async () => {
    const { data } = await create();
    history.push(`/tasks/organize/${data.id}`);
  };

  function validURL(str) {
    var pattern = new RegExp(
      "^(https?:\\/\\/)?" +
        "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" +
        "((\\d{1,3}\\.){3}\\d{1,3}))" +
        "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" +
        "(\\?[;&a-z\\d%_.~+=-]*)?" +
        "(\\#[-a-z\\d_]*)?$",
      "i"
    );
    return !!pattern.test(str);
  }

  const validateWebhook = (e) => {
    const promise = new Promise((resolve, reject) => {
      if (!validURL(e)) {
        resolve({ error: "Webhook URL is not a valid URL." });
      } else if (e.split(":")[0] !== "https") {
        resolve({ error: "Webhook URL must use HTTPS." });
      } else {
        axios
          .post(e, {})
          .then(() => {
            resolve({ success: "Updated Webhook" });
          })
          .catch(() => {
            resolve({
              error: "Webhook is throwing errors to our post requests.",
            });
          });
      }
    });

    return promise;
  };

  return (
    <>
      {
        (game?.cheats || []).length
          ? (
            <RowInfo help={<CheatCodeModal />}>
              <CheatCodes cheats={game?.cheats} />
            </RowInfo>
          )
          : null
      }
      <RowInfo help={<TimeToCompleteModal />}>
        <Input
            help={t('This allows you to cap the length of a game from when a team starts playing.')}
            label={t('Time to Complete')}
            model="games"
            name="time_to_play"
            useQuery={useQuery}
          />
      </RowInfo>
      {super_admin && (
        <RowInfo help={<PromotionModal />}>
          <RadioGroup
              defaultValue={1}
              label="Promotion"
              name="game_type"
              options={[
                { value: 1, label: "Standard" },
                { value: 2, label: "Featured (no password required)" },
                { value: 3, label: "Featured (require password)" },
              ]}
              useQuery={useQuery}
            />
        </RowInfo>
      )}
      <RowInfo help={<SearchModal />}>
        <RadioGroup
            defaultValue={true}
            label="Search"
            name="searchability"
            options={[
              { value: true, label: "Standard" },
              { value: false, label: "Hidden" },
            ]}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<TeamsModal />}>
        <RadioGroup
            defaultValue={false}
            label="Teams"
            name="join_existing_team"
            options={[
              { value: false, label: "Single Device" },
              { value: true, label: "Multiple Devices" },
            ]}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<LeaderboardModal />}>
        <RadioGroup
            defaultValue={true}
            label="Leaderboard"
            name="leaderboard_visible"
            options={[
              { value: true, label: "Visible" },
              { value: false, label: "Hidden" },
            ]}
            useQuery={useQuery}
          />
      </RowInfo>
      {
        decisionMetric?.enabled
            ? (
                <RowInfo help={(
                    <HelpModal title={t(`Leaderboard by`)}>
                        <p>{t(`Leaderboards for a game can be done at the group level, team level or player level. 
                        A group is a set of teams, a team is a set of players, a player is a single individual. 
                        By default, leaderboards will be set to teams.`)}</p>
                    </HelpModal>
                  )}>
                    <RadioGroup
                        defaultValue={true}
                        label={t('Leaderboard by')}
                        name="leaderboard_aggregate"
                        options={[
                          { value: 'groups', label: t('Groups') },
                          { value: 'teams', label: t('Teams') },
                          { value: 'players', label: t('Players') },
                        ]}
                        useQuery={useQuery}
                      />
                </RowInfo>
            )
            : null
      }
      <RowInfo help={<ChatModal />}>
        <RadioGroup
            defaultValue={true}
            label="Chat"
            name="notifications_bidirectional"
            options={[
              { value: true, label: "Enabled" },
              { value: false, label: "Disabled" },
            ]}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<CategoryModal />}>
        <RadioGroup
            label={t('Category')}
            name="category"
            options={[
              { value: "Active", label: "Active" },
              { value: "Draft", label: "Draft" },
              { value: "Template", label: "Template" },
              { value: "Archive", label: "Archive" },
            ]}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<UnlockStyleModal />}>
        <RadioGroup
            defaultValue={false}
            label="Unlock Style"
            name="unlock_requires_network"
            options={[
              { value: false, label: "Offline" },
              { value: true, label: "Online" },
            ]}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<ChallengeListModal />}>
        <RadioGroup
            defaultValue={false}
            label="Challenge List"
            name="shuffled"
            options={[
              { value: false, label: t('Ordered') },
              { value: true, label: t('Shuffled') },
            ]}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<ChallengeViewModal />}>
        <RadioGroup
            defaultValue={false}
            label={t('Challenges')}
            name="challenges_list_view"
            options={[
              { value: 'list', label: t('List View') },
              { value: 'card', label: t('Card View') },
            ]}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={(
        <HelpModal title={t('Push Notifications')}>
          <p>
              {t(`Players that use native iOS or Android apps can receive push notifications. 
                If your game play requires push notifications to be enabled to maximize the user experience, 
                set push notifications to be required. This will add an additional nag to the main menu 
                requesting they enable push notifications in the case they have not provided the necessary 
                permissions. If push notifications are not really required, leave this as optional to avoid 
                the extra nags.`)}
          </p>
        </HelpModal>
      )}>
        <RadioGroup
            defaultValue={false}
            label={t('Push Notifications')}
            name="push_required"
            options={[
              { value: 0, label: t('Optional') },
              { value: 1, label: t('Required') },
            ]}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<ChallengeDropInViewModal />}>
        <FormAutocomplete
            name="post_join_drop_view"
            label={t('Start View')}
            useQuery={useQuery}
            options={[
                { id: 'challenges', name: t('Challenges') },
                { id: 'chat', name: t('Chat') },
                { id: 'custom', name: t('Custom') },
                { id: 'help', name: t('Help') },
                { id: 'leaderboard', name: t('Leaderboard') },
                { id: 'menu', name: t('Main Menu') },
                { id: 'rewards', name: t('Rewards') },
                { id: 'stream', name: t('Stream') },                
            ]}
            size="small"
        />
      </RowInfo>
      <RowInfo help={<ModerationModal />}>
        <FormAutocomplete
            defaultValue="post"
            help="Moderation affects how submissions are released to the stream for other players to see."
            desc={t('Moderation affects how submissions are released to the stream for other players to see.')}
            name="review"
            label="Moderation"
            useQuery={useQuery}
            options={[{
                id: 'pre',
                name: t('Pre-stream'),
            }, {
                id: 'post',
                name: t('Post-stream'),
            }]}
            size="small"
        />
      </RowInfo>
      <RowInfo help={<LateSubmissionsModal />}>
        <FormAutocomplete
            desc={t('Applicable only if your game or challenge is restricted by time.')}
            name="late"
            label="Late Submissions"
            useQuery={useQuery}
            options={[{
                id: '',
                name: ''
            }, {
                id: 'reject',
                name: t('Reject'),
            }, {
                id: 'approve',
                name: t('Approve'),
            }]}
            size="small"
        />
      </RowInfo>
      <RowInfo help={<PlayerExportsModal />}>
        <FormAutocomplete
            desc={t('A popular option for those wanting their players to be able to take home their photography.')}
            name="export_player_enabled"
            label="Player Exports"
            useQuery={useQuery}
            options={[{
                id: false,
                name: t('No, Administrators only'),
            }, {
                id: true,
                name: t('Yes'),
            }]}
            size="small"
        />
      </RowInfo>
      <RowInfo help={<SharingLocationsModal />}>
        <FormAutocomplete
            desc={t('Fine tune the privacy of locations.')}
            label={t('Share player locations')}
            name="share_location"
            useQuery={useQuery}
            options={[{
                id: false,
                name: t('No, withold player locations.'),
            }, {
                id: true,
                name: t('Yes, include maps.'),
            }]}
            size="small"
        />
      </RowInfo>
      <RowInfo help={<WebhookModal />}>
        <Input
            help={t('You can have all our platform game events sent to your own server and application')}
            label="Webhook URL"
            model="games"
            name="webhook"
            maxLength={1024}
            validation={(e) => validateWebhook(e)}
            useQuery={useQuery}
          />
      </RowInfo>
      <Row>
        <Col>
          <ButtonWrapper>
            {game_id === "new" && (
              <Button color="primary" onClick={onCreate}>
                Save{" "}
                {isCreating ? (
                  <Spinner size="0.875rem" />
                ) : (
                  <FontAwesomeIcon icon={faAngleDoubleRight} />
                )}
              </Button>
            )}
          </ButtonWrapper>
        </Col>
      </Row>
    </>
  );
};

export default Optional;
